import type { FileDTO } from "../../api/api";
import { przezmorzeApi } from "../../api/api";
import type { Currency } from "../data/BaseEntity";
import type { ImageFlavour } from "../publicPackages/publicPackagesApi";

interface Tag {
  readonly category: string;
  readonly value: string;
}

interface ECommerceOfferProductOption {
  readonly id: string;
  readonly quantity: number;
  readonly price: number | null;
}

export interface ECommerceOfferProductOptionWithName extends ECommerceOfferProductOption {
  readonly name: string | undefined;
}

export interface ECommerceOfferProduct {
  readonly id: string;
  readonly date: string;
  readonly providerId: string;
  readonly options: ECommerceOfferProductOption[];
  readonly price: number;
  readonly currency: Currency;
}

interface OfferProductImage {
  readonly id: string;
  readonly name: string;
  readonly thumbnail: {
    readonly url: string;
  };
  readonly original: {
    readonly url: string;
  };
}

export interface ECommerceOfferProductWithNames extends ECommerceOfferProduct {
  readonly name: string;
  readonly options: ECommerceOfferProductOptionWithName[];
  readonly description?: {
    readonly polish?: string;
    readonly english?: string;
  };
  readonly contact?: {
    readonly address?: string;
    readonly googlePlaceId?: string;
  };
  readonly images: OfferProductImage[];
}

export interface ListECommerceOffersResponseItem {
  readonly id: { value: string; userFriendly: number };
  readonly name: string;
  readonly info: string;
  readonly start: string | null;
  readonly end: string | null;
  readonly products: ECommerceOfferProduct[];
  readonly files: FileDTO[];
  readonly tags: Tag[];
  readonly image: PackageImageDTO | null;
  readonly map: PackageImageDTO | null;
  readonly days: number | null;
  readonly bookingNumber?: string;
  readonly packageId: string | undefined;
  readonly participantsNumber: { readonly numberOfPaid: number; readonly numberOfStaff: number };
}

export interface PackageImageDTO {
  readonly id: string;
  readonly name: string;
  readonly thumbnail: {
    readonly url: string;
  };
  readonly original: {
    readonly url: string;
  };
  readonly www: {
    readonly url: string;
  };
  readonly flavours: Partial<Record<ImageFlavour, { readonly url: string }>>;
  readonly info?: string;
  readonly source?: string;
}

export interface ECommerceOfferResponse {
  readonly id: { value: string; userFriendly: number };
  readonly name: string;
  readonly info: string | undefined;
  readonly start: string | null;
  readonly participantsNumber: { readonly numberOfPaid: number; readonly numberOfStaff: number };
  readonly currency: Currency;
  readonly price: number | null;
  readonly currencyExchangeRate: number | undefined;
  readonly products: ECommerceOfferProductWithNames[];
  readonly files: FileDTO[];
  readonly tags: Tag[];
  readonly packageId: string | undefined;
}

export async function listECommerceOffers(
  params: { from: string; to?: string },
  abortSignal?: AbortSignal,
): Promise<ListECommerceOffersResponseItem[]> {
  const response = await przezmorzeApi.get<{ items: ListECommerceOffersResponseItem[] }>("/e-commerce/offers", {
    signal: abortSignal,
    params,
  });
  return response.data.items;
}

export async function getECommerceOffer(id: string, abortSignal?: AbortSignal): Promise<ECommerceOfferResponse> {
  const response = await przezmorzeApi.get<ECommerceOfferResponse>(`/e-commerce/offers/${id}`, {
    signal: abortSignal,
  });
  return response.data;
}

export async function bookECommerceOffer(
  offerId: string,
  note: string,
  abortSignal?: AbortSignal,
): Promise<{ orderId: string }> {
  const response = await przezmorzeApi.post<{ orderId: string }>(
    `/e-commerce/offers/${offerId}/bookings`,
    { note },
    {
      signal: abortSignal,
    },
  );
  return response.data;
}
